
import { useState } from "react";
import { useForm } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/components/AuthProvider";

interface FeedbackFormProps {
  onSubmit: () => void;
}

interface FormData {
  type: 'feedback' | 'idea' | 'bug';
  title: string;
  description: string;
}

export const FeedbackForm = ({ onSubmit }: FeedbackFormProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { user } = useAuth();
  const { register, handleSubmit, formState: { errors }, setValue } = useForm<FormData>({
    defaultValues: {
      type: 'feedback'
    }
  });

  const onSubmitForm = async (data: FormData) => {
    console.log('Submitting feedback:', data);
    
    if (!user) {
      toast({
        title: "Authentication required",
        description: "Please sign in to submit feedback",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const { error } = await supabase
        .from('feedback')
        .insert({
          user_id: user.id,
          type: data.type,
          title: data.title,
          description: data.description,
          upvotes: 0,
          downvotes: 0
        });

      if (error) {
        console.error('Supabase error:', error);
        throw error;
      }

      console.log('Feedback submitted successfully');
      toast({
        title: "Thank you for your feedback!",
        description: "Your submission has been received.",
      });
      
      onSubmit();
    } catch (error) {
      console.error('Error submitting feedback:', error);
      toast({
        title: "Error",
        description: "There was a problem submitting your feedback. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmitForm)} className="space-y-6">
      <div className="space-y-4">
        <RadioGroup 
          defaultValue="feedback" 
          onValueChange={(value) => setValue('type', value as 'feedback' | 'idea' | 'bug')}
          className="flex flex-wrap gap-4"
        >
          <div className="flex items-center space-x-2 bg-blue-50 px-4 py-3 rounded-xl hover:bg-blue-100 transition-colors">
            <RadioGroupItem value="feedback" id="feedback" className="text-blue-600 border-blue-400" />
            <Label htmlFor="feedback" className="cursor-pointer font-medium text-blue-700">Feedback</Label>
          </div>
          <div className="flex items-center space-x-2 bg-amber-50 px-4 py-3 rounded-xl hover:bg-amber-100 transition-colors">
            <RadioGroupItem value="idea" id="idea" className="text-amber-600 border-amber-400" />
            <Label htmlFor="idea" className="cursor-pointer font-medium text-amber-700">Idea</Label>
          </div>
          <div className="flex items-center space-x-2 bg-red-50 px-4 py-3 rounded-xl hover:bg-red-100 transition-colors">
            <RadioGroupItem value="bug" id="bug" className="text-red-600 border-red-400" />
            <Label htmlFor="bug" className="cursor-pointer font-medium text-red-700">Bug</Label>
          </div>
        </RadioGroup>

        <div className="space-y-2">
          <Label htmlFor="title" className="text-sm font-semibold text-gray-700">Title</Label>
          <Input
            id="title"
            {...register('title', { required: "Title is required" })}
            placeholder="Brief summary of your feedback"
            className="rounded-xl border-gray-200 focus-visible:ring-blue-500 shadow-sm"
          />
          {errors.title && (
            <p className="text-sm text-red-500">{errors.title.message}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="description" className="text-sm font-semibold text-gray-700">Description</Label>
          <Textarea
            id="description"
            {...register('description', { required: "Description is required" })}
            placeholder="Provide more details about your feedback"
            className="min-h-[150px] rounded-xl border-gray-200 focus-visible:ring-blue-500 shadow-sm resize-none"
          />
          {errors.description && (
            <p className="text-sm text-red-500">{errors.description.message}</p>
          )}
        </div>
      </div>

      <Button 
        type="submit" 
        className="w-full rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 font-semibold py-6 h-auto"
        disabled={isSubmitting}
      >
        {isSubmitting ? "Submitting..." : "Submit Feedback"}
      </Button>
    </form>
  );
};
